"use client";

import { useTranslations } from "next-intl";
import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useSearchHistory } from "@/hooks/use-search-history";

interface AnimatedSearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
  onSearchModeChange?: (isSearchMode: boolean) => void;
  value?: string;
  onQueryChange?: (query: string) => void;
  defaultOpen?: boolean;
  align?: "left" | "right";
  showSearchLabel?: boolean;
  closeOnSubmit?: boolean;
  clearOnSubmit?: boolean;
  clearOnClose?: boolean;
  enableHistory?: boolean;
  overlayPaddingOffset?: number;
  overlayFormClassName?: string;
  overlayInputWrapperClassName?: string;
  reduceMotion?: boolean;
  onDarkMedia?: boolean;
  /**
   * Extra px to shift the *collapsed* trigger inward from the `align` edge, so
   * it can sit beside a trailing element (e.g. the mobile header avatar) without
   * overlapping it. Only applied while collapsed; the expanded pill returns to
   * the edge (the trailing element hides in search mode). Right-align only.
   */
  collapsedRightOffset?: number;
}

export function AnimatedSearchBar({
  onSearch,
  placeholder: placeholderProp,
  className,
  onSearchModeChange,
  value,
  onQueryChange,
  defaultOpen = false,
  align = "right",
  showSearchLabel = true,
  closeOnSubmit = true,
  clearOnSubmit = true,
  clearOnClose = true,
  enableHistory = true,
  overlayPaddingOffset = 0,
  overlayFormClassName,
  overlayInputWrapperClassName,
  reduceMotion = false,
  onDarkMedia = false,
  collapsedRightOffset = 0,
}: AnimatedSearchBarProps) {
  const tFeed = useTranslations("feedTabs");
  const placeholder = placeholderProp ?? tFeed("searchPlaceholder");
  const searchLabel = tFeed("searchLabel");
  const [isSearchMode, setIsSearchMode] = useState(defaultOpen);
  const [internalQuery, setInternalQuery] = useState("");
  const { addToHistory } = useSearchHistory();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isControlled = typeof value === "string" && typeof onQueryChange === "function";
  const searchQuery = isControlled ? value : internalQuery;
  const setSearchMode = (next: boolean) => {
    setIsSearchMode(next);
    onSearchModeChange?.(next);
  };
  const setSearchQuery = (next: string) => {
    if (isControlled) {
      onQueryChange?.(next);
      return;
    }
    setInternalQuery(next);
  };

  useEffect(() => {
    if (searchInputRef.current && document.activeElement === searchInputRef.current && !isSearchMode) {
      searchInputRef.current.blur();
    }
  }, []);

  useEffect(() => {
    if (isSearchMode && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (!isSearchMode && searchInputRef.current && document.activeElement === searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [isSearchMode, onSearchModeChange]);

  useEffect(() => {
    if (!defaultOpen) return;
    if (isSearchMode) return;
    setSearchMode(true);
  }, [defaultOpen, isSearchMode]);

  const hasSearchText = searchQuery.trim().length > 0;
  const showClearButton = searchQuery.trim().length >= 2;
  const canShowSearchLabel = showSearchLabel && hasSearchText;
  const searchControlTargetWidth = !isSearchMode ? 40 : canShowSearchLabel ? 92 : 40;
  const searchOverlayPadding = overlayPaddingOffset + searchControlTargetWidth;
  const overlayPaddingStyle =
    align === "right"
      ? { paddingRight: searchOverlayPadding }
      : { paddingLeft: searchOverlayPadding };
  const buttonPositionClass = align === "right" ? "right-4" : "left-4";
  // Collapsed trigger inset from the right edge (base 16px = `right-4`), shifted
  // inward while collapsed so it clears a trailing element (mobile avatar).
  const collapsedRightPx =
    align === "right" && !isSearchMode ? 16 + collapsedRightOffset : 16;
  const inputOriginX = align === "right" ? 1 : 0;
  const clearButtonRight = align === "right" ? searchOverlayPadding + 8 : 8;

  const submitSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    if (enableHistory) {
      addToHistory(trimmed);
    }
    onSearch(trimmed);
    if (closeOnSubmit) {
      setSearchMode(false);
    }
    if (clearOnSubmit) {
      setSearchQuery("");
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitSearch(searchQuery);
  };

  if (reduceMotion) {
    return (
      <>
        <button
          onClick={() => {
            if (!isSearchMode) {
              setSearchMode(true);
              return;
            }
            if (canShowSearchLabel) {
              submitSearch(searchQuery);
              return;
            }
            setSearchMode(false);
            if (clearOnClose) {
              setSearchQuery("");
            }
          }}
          aria-label={
            !isSearchMode ? "Open search" : canShowSearchLabel ? searchLabel : "Close search"
          }
          style={{
            width: searchControlTargetWidth,
            paddingLeft: isSearchMode && canShowSearchLabel ? 16 : 0,
            paddingRight: isSearchMode && canShowSearchLabel ? 16 : 0,
            ...(align === "right" ? { right: collapsedRightPx } : {}),
          }}
          className={cn(
            "absolute top-1/2 -translate-y-1/2 z-40 shrink-0 h-10 rounded-full flex items-center justify-center border focus:outline-none active:scale-95 transition-none overflow-hidden",
            buttonPositionClass,
            isSearchMode && canShowSearchLabel
              ? "bg-white text-black border-black/10 hover:bg-white/90 font-semibold"
              : onDarkMedia
                ? "backdrop-blur-md bg-white/15 border-white/35"
                : "backdrop-blur-md bg-background/30 border-border/20",
            className
          )}
        >
          {!isSearchMode ? (
            <Search className={cn("w-5 h-5 md:w-8 md:h-8", onDarkMedia ? "text-white" : "text-foreground")} />
          ) : canShowSearchLabel ? (
            <span className="text-sm">{searchLabel}</span>
          ) : (
            <Search className={cn("w-5 h-5 md:w-8 md:h-8", onDarkMedia ? "text-white" : "text-foreground")} />
          )}
        </button>

        <div
          className={cn(
            "absolute inset-0 z-30 flex items-center gap-1.5 px-4",
            isSearchMode ? "opacity-100" : "opacity-0"
          )}
          style={{ pointerEvents: isSearchMode ? "auto" : "none" }}
        >
          <form onSubmit={handleSearchSubmit} className={cn("flex w-full items-center", overlayFormClassName)}>
            <div className={cn("relative flex-1", overlayInputWrapperClassName)} style={overlayPaddingStyle}>
              <input
                ref={searchInputRef}
                type="text"
                inputMode="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={placeholder}
                enterKeyHint="search"
                tabIndex={isSearchMode ? 0 : -1}
                disabled={!isSearchMode}
                className={cn(
                  "w-full h-10 pl-4 rounded-full backdrop-blur-md focus:outline-none focus:ring-2 disabled:opacity-0",
                  showClearButton ? "pr-10" : "pr-4",
                  onDarkMedia
                    ? "bg-white/15 border border-white/30 text-white placeholder:text-white/50 focus:ring-white/30"
                    : "bg-background/30 border border-border/20 focus:ring-foreground/20"
                )}
              />
              {showClearButton ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                  onMouseDown={(e) => e.preventDefault()}
                  style={{ right: clearButtonRight }}
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center focus:outline-none",
                    onDarkMedia
                      ? "text-white/85 hover:text-white hover:bg-white/15"
                      : "text-foreground/80 hover:text-foreground hover:bg-background/60"
                  )}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <motion.button
        style={align === "right" ? { right: collapsedRightPx } : undefined}
        animate={{
          width: searchControlTargetWidth,
          paddingLeft: isSearchMode && canShowSearchLabel ? 16 : 0,
          paddingRight: isSearchMode && canShowSearchLabel ? 16 : 0,
        }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        onClick={() => {
          if (!isSearchMode) {
            setSearchMode(true);
            return;
          }
          if (canShowSearchLabel) {
            submitSearch(searchQuery);
            return;
          }
          setSearchMode(false);
          if (clearOnClose) {
            setSearchQuery("");
          }
        }}
        aria-label={
          !isSearchMode
            ? "Open search"
            : canShowSearchLabel
              ? searchLabel
              : "Close search"
        }
        className={cn(
          "absolute top-1/2 -translate-y-1/2 z-40 shrink-0 h-10 rounded-full flex items-center justify-center border focus:outline-none active:scale-95 transition-transform overflow-hidden",
          buttonPositionClass,
          isSearchMode && canShowSearchLabel
            ? "bg-white text-black border-black/10 hover:bg-white/90 font-semibold"
            : onDarkMedia
              ? "backdrop-blur-md bg-white/15 border-white/35"
              : "backdrop-blur-md bg-background/30 border-border/20",
          className
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          {!isSearchMode ? (
            <motion.div
              key="search-icon"
              initial={{ opacity: 0, scale: 0.9, rotate: -90 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.9, rotate: 90 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="flex items-center justify-center"
            >
              <Search className={cn("w-5 h-5 md:w-8 md:h-8", onDarkMedia ? "text-white" : "text-foreground")} />
            </motion.div>
          ) : canShowSearchLabel ? (
            <motion.span
              key="search-pill"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="text-sm"
            >
              {searchLabel}
            </motion.span>
          ) : (
            <motion.div
              key="search-empty"
              initial={{ opacity: 0, scale: 0.9, rotate: -90 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.9, rotate: 90 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="flex items-center justify-center"
            >
              <Search className={cn("w-5 h-5 md:w-8 md:h-8", onDarkMedia ? "text-white" : "text-foreground")} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      <motion.div
        className="absolute inset-0 z-30 flex items-center gap-1.5 px-4"
        animate={{ opacity: isSearchMode ? 1 : 0, y: isSearchMode ? 0 : -2 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        style={{ pointerEvents: isSearchMode ? "auto" : "none" }}
      >
        <motion.form
          onSubmit={handleSearchSubmit}
          className={cn("flex w-full items-center", overlayFormClassName)}
        >
          <motion.div
            animate={overlayPaddingStyle}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className={cn("relative flex-1", overlayInputWrapperClassName)}
          >
            <motion.div
              style={{ originX: inputOriginX }}
              animate={{ scaleX: isSearchMode ? 1 : 0.02 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <input
                ref={searchInputRef}
                type="text"
                inputMode="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={placeholder}
                enterKeyHint="search"
                tabIndex={isSearchMode ? 0 : -1}
                disabled={!isSearchMode}
                className={cn(
                  "w-full h-10 pl-4 rounded-full backdrop-blur-md focus:outline-none focus:ring-2 disabled:opacity-0",
                  showClearButton ? "pr-10" : "pr-4",
                  onDarkMedia
                    ? "bg-white/15 border border-white/30 text-white placeholder:text-white/50 focus:ring-white/30"
                    : "bg-background/30 border border-border/20 focus:ring-foreground/20"
                )}
              />
            </motion.div>
            <AnimatePresence initial={false}>
              {showClearButton ? (
                <motion.button
                  key="clear-input"
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  aria-label="Clear search"
                  style={{ right: clearButtonRight }}
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center focus:outline-none",
                    onDarkMedia
                      ? "text-white/85 hover:text-white hover:bg-white/15"
                      : "text-foreground/80 hover:text-foreground hover:bg-background/60"
                  )}
                >
                  <X className="h-3.5 w-3.5" />
                </motion.button>
              ) : null}
            </AnimatePresence>
          </motion.div>
        </motion.form>
      </motion.div>
    </>
  );
}

